import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Calendar,
  CheckSquare,
  StickyNote,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Sparkles,
  Plus,
  Circle,
  CheckCircle2,
} from 'lucide-react';
import { apiGet, apiPost, apiPatch } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface DayMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  duration_minutes: number | null;
  series_name: string | null;
  series_id: string | null;
}

interface SuggestedMeeting {
  series_id: string;
  series_name: string;
  time: string | null;
  duration: number | null;
}

interface DayTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  created_at?: string;
}

interface DayNote {
  id: string;
  title: string;
  updated_at: string;
}

interface DayData {
  meetings: DayMeeting[];
  suggestedMeetings: SuggestedMeeting[];
  tasksDue: DayTask[];
  tasksCreated: DayTask[];
  notesUpdated: DayNote[];
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f59e0b',
  medium: '#6366f1',
  low: '#636b88',
};

const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

const STATUS_COLORS: Record<string, string> = {
  todo: '#636b88',
  in_progress: '#6366f1',
  blocked: '#ef4444',
  done: '#22c55e',
  cancelled: '#636b88',
};

function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDuration(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDateHeading(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + n);
  const ny = date.getFullYear();
  const nm = String(date.getMonth() + 1).padStart(2, '0');
  const nd = String(date.getDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

function getLocalToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getMeetingUrl(meeting: DayMeeting): string {
  if (meeting.series_id) return `/meetings/${meeting.series_id}/${meeting.id}`;
  return `/meetings/standalone/${meeting.id}`;
}

export function DayViewPage() {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<DayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [creatingSuggestion, setCreatingSuggestion] = useState<string | null>(null);
  const [togglingTask, setTogglingTask] = useState<string | null>(null);

  function loadDay() {
    if (!date) return;
    setLoading(true);
    apiGet<DayData>(`/calendar/day/${date}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadDay(); }, [date]);

  async function handleCreateSuggestion(suggestion: SuggestedMeeting) {
    setCreatingSuggestion(suggestion.series_id);
    try {
      await apiPost(`/meeting-series/${suggestion.series_id}/instances`, {
        meeting_date: date,
        meeting_time: suggestion.time,
        duration_minutes: suggestion.duration,
      });
      toast.success('Meeting created');
      loadDay();
    } catch {
      toast.error('Failed to create meeting');
    } finally {
      setCreatingSuggestion(null);
    }
  }

  async function handleToggleTask(taskId: string, currentStatus: string) {
    setTogglingTask(taskId);
    const newStatus = currentStatus === 'done' ? 'todo' : 'done';
    // Optimistic update
    if (data) {
      const updateTasks = (tasks: DayTask[]) =>
        tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t);
      setData({
        ...data,
        tasksDue: updateTasks(data.tasksDue),
        tasksCreated: updateTasks(data.tasksCreated),
      });
    }
    try {
      await apiPatch(`/tasks/${taskId}`, { status: newStatus });
    } catch {
      toast.error('Failed to update task');
      loadDay(); // revert on error
    } finally {
      setTogglingTask(null);
    }
  }

  if (!date) return null;

  const isToday = date === getLocalToday();
  const dayViewState = { from: `/day/${date}`, fromLabel: 'Day View' };

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(`/day/${addDays(date, -1)}`)}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-text-primary">{formatDateHeading(date)}</h1>
          </div>
          <button
            onClick={() => navigate(`/day/${getLocalToday()}`)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              isToday
                ? 'bg-accent/10 text-accent cursor-default'
                : 'bg-bg-hover text-text-secondary hover:text-text-primary hover:bg-bg-input'
            }`}
            disabled={isToday}
          >
            Today
          </button>
          <button
            onClick={() => navigate(`/day/${addDays(date, 1)}`)}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={24} className="text-accent animate-spin" />
          </div>
        ) : !data ? (
          <p className="text-text-muted text-center py-16">Failed to load day data.</p>
        ) : (
          <div className="space-y-6">
            {/* Meetings */}
            {data.meetings.length > 0 && (
              <DaySection icon={Calendar} title="Meetings" count={data.meetings.length} iconColor="#f59e0b">
                {data.meetings.map(m => (
                  <Link
                    key={m.id}
                    to={getMeetingUrl(m)}
                    state={dayViewState}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-warning/10">
                      <Calendar size={16} className="text-warning" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary font-medium truncate group-hover:text-accent transition-colors">
                        {m.title || m.series_name || 'Meeting'}
                      </p>
                      <p className="text-[10px] text-text-muted">
                        {m.meeting_time && formatTime(m.meeting_time)}
                        {m.meeting_time && m.duration_minutes && ` \u00B7 ${formatDuration(m.duration_minutes)}`}
                        {m.series_name && m.title && ` \u00B7 ${m.series_name}`}
                      </p>
                    </div>
                  </Link>
                ))}
              </DaySection>
            )}

            {/* Suggested Meetings */}
            {data.suggestedMeetings.length > 0 && (
              <DaySection icon={Sparkles} title="Suggested Meetings" count={data.suggestedMeetings.length} iconColor="#818cf8">
                {data.suggestedMeetings.map(s => (
                  <div
                    key={s.series_id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-border"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border border-dashed border-accent/40">
                      <Calendar size={16} className="text-accent/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-secondary font-medium truncate">
                        {s.series_name}
                      </p>
                      <p className="text-[10px] text-text-muted">
                        {s.time && formatTime(s.time)}
                        {s.time && s.duration && ` \u00B7 ${formatDuration(s.duration)}`}
                      </p>
                    </div>
                    <button
                      onClick={() => handleCreateSuggestion(s)}
                      disabled={creatingSuggestion === s.series_id}
                      className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-accent/10 text-accent rounded-md hover:bg-accent/20 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      <Plus size={12} />
                      {creatingSuggestion === s.series_id ? 'Creating...' : 'Create Meeting'}
                    </button>
                  </div>
                ))}
              </DaySection>
            )}

            {/* Tasks Due */}
            {data.tasksDue.length > 0 && (
              <DaySection icon={CheckSquare} title="Tasks Due" count={data.tasksDue.length} iconColor="#6366f1">
                {data.tasksDue.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors group">
                    <button
                      onClick={() => handleToggleTask(t.id, t.status)}
                      disabled={togglingTask === t.id}
                      className="flex-shrink-0 text-text-muted hover:text-success transition-colors disabled:opacity-50"
                      title={t.status === 'done' ? 'Mark incomplete' : 'Mark complete'}
                    >
                      {t.status === 'done' ? (
                        <CheckCircle2 size={16} className="text-success" />
                      ) : (
                        <Circle size={16} />
                      )}
                    </button>
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PRIORITY_COLORS[t.priority] || '#636b88' }}
                    />
                    <Link
                      to={`/tasks/${t.id}`}
                      state={dayViewState}
                      className="flex-1 min-w-0"
                    >
                      <p className={`text-sm truncate group-hover:text-accent transition-colors ${
                        t.status === 'done' ? 'text-text-muted line-through' : 'text-text-primary'
                      }`}>
                        {t.title}
                      </p>
                    </Link>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                      style={{
                        backgroundColor: (STATUS_COLORS[t.status] || '#636b88') + '20',
                        color: STATUS_COLORS[t.status] || '#636b88',
                      }}
                    >
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </div>
                ))}
              </DaySection>
            )}

            {/* Tasks Created */}
            {data.tasksCreated.length > 0 && (
              <DaySection icon={CheckSquare} title="Tasks Created" count={data.tasksCreated.length} iconColor="#22c55e">
                {data.tasksCreated.map(t => (
                  <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors group">
                    <button
                      onClick={() => handleToggleTask(t.id, t.status)}
                      disabled={togglingTask === t.id}
                      className="flex-shrink-0 text-text-muted hover:text-success transition-colors disabled:opacity-50"
                      title={t.status === 'done' ? 'Mark incomplete' : 'Mark complete'}
                    >
                      {t.status === 'done' ? (
                        <CheckCircle2 size={16} className="text-success" />
                      ) : (
                        <Circle size={16} />
                      )}
                    </button>
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PRIORITY_COLORS[t.priority] || '#636b88' }}
                    />
                    <Link
                      to={`/tasks/${t.id}`}
                      state={dayViewState}
                      className="flex-1 min-w-0"
                    >
                      <p className={`text-sm truncate group-hover:text-accent transition-colors ${
                        t.status === 'done' ? 'text-text-muted line-through' : 'text-text-primary'
                      }`}>
                        {t.title}
                      </p>
                    </Link>
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                      style={{
                        backgroundColor: (STATUS_COLORS[t.status] || '#636b88') + '20',
                        color: STATUS_COLORS[t.status] || '#636b88',
                      }}
                    >
                      {STATUS_LABELS[t.status] || t.status}
                    </span>
                  </div>
                ))}
              </DaySection>
            )}

            {/* Notes Updated */}
            {data.notesUpdated.length > 0 && (
              <DaySection icon={StickyNote} title="Notes Updated" count={data.notesUpdated.length} iconColor="#14b8a6">
                {data.notesUpdated.map(n => (
                  <Link
                    key={n.id}
                    to={`/notes/${n.id}`}
                    state={dayViewState}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-bg-hover transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#14b8a615' }}>
                      <StickyNote size={16} style={{ color: '#14b8a6' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-primary truncate group-hover:text-accent transition-colors">
                        {n.title}
                      </p>
                      <p className="text-[10px] text-text-muted">
                        {new Date(n.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </Link>
                ))}
              </DaySection>
            )}

            {/* Empty state */}
            {data.meetings.length === 0 && data.suggestedMeetings.length === 0 &&
             data.tasksDue.length === 0 && data.tasksCreated.length === 0 &&
             data.notesUpdated.length === 0 && (
              <div className="text-center py-16 text-text-muted">
                <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg mb-2">Nothing on this day</p>
                <p className="text-sm">No meetings, tasks, or notes for this date.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DaySection({
  icon: Icon,
  title,
  count,
  iconColor,
  children,
}: {
  icon: typeof Calendar;
  title: string;
  count: number;
  iconColor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} style={{ color: iconColor }} />
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bg-hover text-text-muted font-medium">
          {count}
        </span>
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
